import { minutesToMilliseconds, secondsToMilliseconds } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';

import { logger } from '../../datadog/logger';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { appointmentInfoAtom } from '../../state';
import { toast } from '../../toasts';
import { UserType } from '../../types';
import { DataChannelMessageType, useSendMessage } from '../../video-sdk';
import { hasRecordingAtom } from '../session-recording/state';
import { isRestartingSessionAtom, providerShortIdAtom } from '../state';
import { useSessionSummaryNudgeCount } from '../useSessionSummaryNudgeCount';
import { useCompleteSessionCallback } from './useCompleteSessionCallback';

const EVENT_PAIRS: Record<string, [TrackingEvents, EventTypeEnum]> = {
  earlyEndComplete: [
    TrackingEvents.PROVIDER_SESSION_EARLY_END_COMPLETE,
    EventTypeEnum.PROVIDER_SESSION_EARLY_END_COMPLETE,
  ],
  earlyEndRestart: [
    TrackingEvents.PROVIDER_SESSION_EARLY_END_RESTART,
    EventTypeEnum.PROVIDER_SESSION_EARLY_END_RESTART,
  ],
  complete: [TrackingEvents.PROVIDER_SESSION_COMPLETE, EventTypeEnum.PROVIDER_SESSION_COMPLETE],
};

const EARLY_END_THRESHOLD_MS = minutesToMilliseconds(5);
const WAIT_TIME_BEFORE_RELOAD_MS = secondsToMilliseconds(3);

/**
 * Custom hook that returns a set of handlers for the `CompleteSessionButton`:
 * - checkShouldShowEarlyEndModal: returns a boolean indicating whether a
 *   confirmation modal should be shown before completing the session
 * - handleCompleteSession: logs an event and completes the session
 * - handleCompleteSessionEarly: logs an event and completes the session
 * - handleRestartSession: logs an event, sends a data track message to
 *   client(s), and performs a full page reload
 */
export function useHandleCompleteSessionCallbacks({
  reload = () => window.location.reload(),
  waitBeforeReloadMs = WAIT_TIME_BEFORE_RELOAD_MS,
}: {
  reload?: () => void;
  waitBeforeReloadMs?: number;
} = {}) {
  const shouldDisableTranscription = useShouldDisableTranscriptionForMultipleParticipants();
  const completeVideoSession = useCompleteSessionCallback();
  const getHasRecording = useAtomCallback(useCallback((get) => get(hasRecordingAtom), []));
  const setHasRecording = useSetAtom(hasRecordingAtom);
  const { disable: disableFutureSessionSummaryNudges } = useSessionSummaryNudgeCount();
  const getAppointmentInfo = useAtomCallback(useCallback((get) => get(appointmentInfoAtom), []));
  const sendReturnToWaitingRoomMessage = useSendReturnToWaitingRoomMessageCallback();
  const setIsRestartingSession = useSetAtom(isRestartingSessionAtom);
  const { userClicked } = useTrackEvent();

  const checkShouldShowEarlyEndModal = useCallback((): boolean => {
    const appointmentInfo = getAppointmentInfo();

    // If there is no active appointment set, completing the session doesn't
    // actually entail most side effects, so we don't need to show the
    // confirmation modal.
    if (!appointmentInfo?.timeEnd) {
      return false;
    }

    // Otherwise, we'll check and return whether the appointment is scheduled to
    // end more than 5 minutes from now.
    const scheduledEndTime = new Date(appointmentInfo.timeEnd).getTime();
    return scheduledEndTime - Date.now() > EARLY_END_THRESHOLD_MS;
  }, [getAppointmentInfo]);

  const completeSession = useCallback(
    (eventPair: [TrackingEvents, EventTypeEnum]) => {
      const [loggingEvent, segmentEvent] = eventPair;
      sendLoggingEvents(loggingEvent);
      const appointmentShortId = getAppointmentInfo()?.shortId ?? '';
      userClicked(segmentEvent, { appointmentShortId });

      completeVideoSession();
      disableFutureSessionSummaryNudges();

      toast.success(
        getHasRecording() && !shouldDisableTranscription
          ? 'Session completed. Transcription processing in progress.'
          : 'Session completed.',
        { onClose: () => setHasRecording(false) },
      );
    },
    [
      completeVideoSession,
      disableFutureSessionSummaryNudges,
      getAppointmentInfo,
      getHasRecording,
      setHasRecording,
      shouldDisableTranscription,
      userClicked,
    ],
  );

  const handleCompleteSession = useCallback(() => completeSession(EVENT_PAIRS.complete), [completeSession]);

  const handleCompleteSessionEarly = useCallback(
    () => completeSession(EVENT_PAIRS.earlyEndComplete),
    [completeSession],
  );

  const handleRestartSession = useCallback(async () => {
    const [loggingEvent, segmentEvent] = EVENT_PAIRS.earlyEndRestart;
    sendLoggingEvents(loggingEvent);
    const appointmentShortId = getAppointmentInfo()?.shortId ?? '';
    userClicked(segmentEvent, { appointmentShortId });

    setIsRestartingSession(true);
    await sendReturnToWaitingRoomMessage();

    // NOTE: If the provider reload started immediately, there would be a race
    // condition between our data track message arriving and the client(s) being
    // unsubscribed from the data track due to their provider leaving the
    // session. To ensure the client receives the message successfully first, we
    // delay the reload for up to WAIT_TIME_BEFORE_RELOAD_MS milliseconds. If
    // the client(s) disconnect, the provider's session will be reloaded
    // automatically by the listeners for the room disconnect event, based on
    // the state we set above. As a fallback, we'll reload the provider's page
    // here after the delay, just in case the message never made it.
    await new Promise((resolve) => setTimeout(resolve, waitBeforeReloadMs));
    reload();
  }, [
    getAppointmentInfo,
    reload,
    sendReturnToWaitingRoomMessage,
    setIsRestartingSession,
    userClicked,
    waitBeforeReloadMs,
  ]);

  return {
    checkShouldShowEarlyEndModal,
    handleCompleteSession,
    handleCompleteSessionEarly,
    handleRestartSession,
  };
}

/**
 * Sends a data track message to client(s) to indicate the provider's intention
 * to restart the session. Normally, when the provider leaves the session, the
 * client is also automatically disconnected and redirected to the post-session
 * flow. We don't want this to happen when the provider is just restarting their
 * session, as the provider's goal is to immediately re-admit their client(s)
 * and continue the session from there. This data track message informs the
 * client(s) of this intention, preventing the automatic post-session redirect
 * and triggering their pages to reload as well.
 */
function useSendReturnToWaitingRoomMessageCallback() {
  const sendDataTrackMessage = useSendMessage();
  const getProviderShortId = useAtomCallback(useCallback((get) => get(providerShortIdAtom), []));
  return useCallback(async () => {
    const providerShortId = getProviderShortId();
    if (!providerShortId) {
      logger.warn('Attempted to send RESTART_SESSION message without provider short ID');
      return;
    }
    try {
      await sendDataTrackMessage({
        type: DataChannelMessageType.RESTART_SESSION,
        sender: {
          entityType: UserType.PROVIDER,
          entityId: providerShortId,
        },
      });
    } catch (error) {
      logger.error('Failed to send RESTART_SESSION message', { providerShortId }, error as Error);
    }
  }, [getProviderShortId, sendDataTrackMessage]);
}
